import { Injectable } from "@angular/core";

import { Observable, Subscriber } from 'rxjs';
import { take } from 'rxjs/operators';

import { DialogService, MessagePlaceholders, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../constants/api-endpoints.constants';

import { ExcelSheetColumnDataType } from "../interfaces/expected-sheet-configuration.interface";
import { ExcelSheetColumnValidationErrorType, ParsedExcelSheet } from "../interfaces/parsed-excel-sheet.interface";

import { ReferenceDataCategory } from '../models/reference-data-category';

import { AppDataService } from '../services/app-data.service';
import { ErrorService } from '../services/error.service';
import { ExcelParserService } from "./excel-parser.service";
import { DialogHelperService } from "./dialog-helper.service";
import { HtmlHelperService } from "./html-helper.service";
import { APP_DATA_KEYS } from "../constants/app-data.constants";
import { CareAllowanceRate } from "../models/care-allowance-rate";
import { DataTable } from "../interfaces/data-table.interface";

class ExcelImportError {
  constructor(
    public popupCode: string,
    public data?: MessagePlaceholders,
    public placeholders?: MessagePlaceholders) {
  }
};

@Injectable({
  providedIn: 'root'
})
export class ExcelImportService {

  constructor(
    private errorService: ErrorService,
    private excelParserService: ExcelParserService,
    private dialogHelperService: DialogHelperService,
    private spinnerService: SpinnerService,
    private dialogService: DialogService,
    private appDataService: AppDataService,
    private htmlHelperService: HtmlHelperService
  ) {
  }

  importCareAllowanceRatesFromCsv(file: File): Observable<void> {
    return this.uploadSheet(file, 'rateUploadFileType', (subscriber, event) => {
      const sheet = this.excelParserService.parseSheet(event.target.result, {
        columns: [
          { headerName: 'Financial Year Code', isMandatory: true, isMandatoryErrorCode: 'rateUploadFinYearCodeNotFound' },
          { headerName: 'Placement Type Code', isMandatory: true, isMandatoryErrorCode: 'rateUploadPlacementTypeCodeNotFound' },
          { headerName: 'Level Code', isMandatory: true, isMandatoryErrorCode: 'rateUploadLevelCodeNotFound' },
          { headerName: 'Age Min', isMandatory: true, isMandatoryErrorCode: 'rateUploadAgeMinNotFound', dataType: ExcelSheetColumnDataType.Integer, dataTypeErrorCode: 'rateUploadAgeMinInvalid' },
          { headerName: 'Age Max', isMandatory: true, isMandatoryErrorCode: 'rateUploadAgeMaxNotFound', dataType: ExcelSheetColumnDataType.Integer, dataTypeErrorCode: 'rateUploadAgeMaxInvalid' },
          { headerName: 'Rate Amount', isMandatory: true, isMandatoryErrorCode: 'rateUploadAmountNotFound', dataType: ExcelSheetColumnDataType.Number, dataTypeErrorCode: 'rateUploadRateAmountInvalid' },
        ]
      });
      if (!this.isSheetValid(subscriber, sheet, 'rateUploadCsvExpectedColumnHeaders', 'rateUploadCsvExpectedColumns', 'rateUploadNoData')) {
        return;
      }

      const entities: CareAllowanceRate[] = [];
      for (let i = 2; i <= sheet.actualLastRowNumber; i++) {

        // Read the row data
        const financialYearCode = sheet.getCellValue('Financial Year Code', i);
        const placementTypeCode = sheet.getCellValue('Placement Type Code', i);
        const levelCode = sheet.getCellValue('Level Code', i);
        const ageMin = sheet.getCellValue('Age Min', i);
        const ageMax = sheet.getCellValue('Age Max', i);
        const rateAmount = sheet.getCellValue('Rate Amount', i);

        // Add the row data to the array of entities
        const entity: CareAllowanceRate = {
          financialYearCode: financialYearCode,
          placementTypeCode: placementTypeCode,
          levelCode: levelCode,
          ageMin: this.excelParserService.toInteger(ageMin),
          ageMax: this.excelParserService.toInteger(ageMax),
          rateAmount: this.excelParserService.toNumber(rateAmount),
        };
        entities.push(entity);
      }

      this.postSheetData(subscriber, API_ENDPOINTS.careAllowanceRate.bulkSave, entities, 'rateUploadSuccessful');
    });
  }

  importReferenceDataFromCsv(file: File): Observable<void> {
    return this.uploadSheet(file, 'refDataUploadFileType', (subscriber, event) => {

      const sheet = this.excelParserService.parseSheet(event.target.result, {
        columns: [
          { headerName: 'CategoryCode', isMandatory: true, isMandatoryErrorCode: 'refDataCategoryCodeNotFound' },
          { headerName: 'CategoryText', isMandatory: true, isMandatoryErrorCode: 'refDataCategoryTextNotFound' },
          { headerName: 'IsCategoryActive', isMandatory: true, isMandatoryErrorCode: 'refDataUploadIsCategoryActiveInvalid', dataType: ExcelSheetColumnDataType.Boolean, dataTypeErrorCode: 'refDataUploadIsCategoryActiveInvalid' },
          { headerName: 'ParentCategoryCode' },
          { headerName: 'ValueCode', isMandatory: true, isMandatoryErrorCode: 'refDataValueCodeNotFound' },
          { headerName: 'ValueText', isMandatory: true, isMandatoryErrorCode: 'refDataValueTextNotFound' },
          { headerName: 'IsValueActive', isMandatory: true, isMandatoryErrorCode: 'refDataUploadIsValueActiveInvalid', dataType: ExcelSheetColumnDataType.Boolean, dataTypeErrorCode: 'refDataUploadIsValueActiveInvalid' },
          { headerName: 'DisplayOrder', isMandatory: true, isMandatoryErrorCode: 'refDataUploadDisplayOrderNotFound', dataType: ExcelSheetColumnDataType.Integer, dataTypeErrorCode: 'refDataUploadDisplayOrderInvalid' },
          { headerName: 'ParentValueCode' }
        ]
      });
      if (!this.isSheetValid(subscriber, sheet, 'refDataUploadCsvExpectedColumnHeaders', 'refDataUploadCsvExpectedColumns', 'refDataUploadNoData')) {
        return;
      }

      const entities: ReferenceDataCategory[] = [];
      for (let i = 2; i <= sheet.actualLastRowNumber; i++) {

        // Read the row data
        const categoryCode = sheet.getCellValue('CategoryCode', i);
        const categoryText = sheet.getCellValue('CategoryText', i);
        const isCategoryActive = sheet.getCellValue('IsCategoryActive', i);
        const parentCategoryCode = sheet.getCellValue('ParentCategoryCode', i);
        const valueCode = sheet.getCellValue('ValueCode', i);
        const valueText = sheet.getCellValue('ValueText', i);
        const isValueActive = sheet.getCellValue('IsValueActive', i);
        const displayOrder = sheet.getCellValue('DisplayOrder', i);
        const parentValueCode = sheet.getCellValue('ParentValueCode', i);

        // Add the row data to the array of entities
        entities.push({
          code: categoryCode,
          text: categoryText,
          isActive: this.excelParserService.toBoolean(isCategoryActive),
          parentCategoryCode: parentCategoryCode,
          value: {
            code: valueCode,
            text: valueText,
            isActive: this.excelParserService.toBoolean(isValueActive),
            displayOrder: this.excelParserService.toInteger(displayOrder),
            parentValueCode: parentValueCode
          }
        });
      }

      this.postSheetData(subscriber, API_ENDPOINTS.referenceData.bulkSave, entities, 'refDataUploadSuccessful');
    });
  }

  importBulkApplicationsDataFromXlsx(file: File, errorPopup: string): Observable<DataTable[]> {
    return this.uploadAndParseWorkBook(file, errorPopup);
  }

  private uploadAndParseWorkBook(file: File, importFileTypePopupCode: string): Observable<DataTable[]> {

    return this.uploadWorkBook<DataTable[]>(file, importFileTypePopupCode, (subscriber, event) => {

      const dataTables = this.excelParserService.parseWorkBook(event.target.result)
      if (!dataTables || dataTables.length === 0) {
        subscriber.error();
      }

      subscriber.next(dataTables);
      subscriber.complete();
    });
  }

  private uploadWorkBook<T>(file: File, fileTypeErrorPopupCode: string, processFunction: (subscriber: Subscriber<T>, event: ProgressEvent<FileReader>) => void): Observable<T> {

    return new Observable(subscriber => {
      try {
        const reader = new FileReader();
        reader.onload = event => {
          try {
            processFunction(subscriber, event);
          }
          catch (error) {
            subscriber.error(error);
          }
        };
        if (file) {
          const validFileTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
          ];
          if (validFileTypes.includes(file.type)) {
            reader.readAsBinaryString(file);
          }
          else {
            subscriber.error(new ExcelImportError(fileTypeErrorPopupCode));
          }
        }
      }
      catch (error) {
        subscriber.error(error);
      }
    });
  }

  private uploadSheet(file: File, fileTypeErrorPopupCode: string, processFunction: (subscriber: Subscriber<void>, event: ProgressEvent<FileReader>) => void): Observable<void> {
    return new Observable(subscriber => {
      try {
        const reader = new FileReader();
        reader.onload = event => {
          try {
            processFunction(subscriber, event);
          }
          catch (error) {
            subscriber.error(error);
          }
        };
        if (file) {
          const validFileTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
          ];
          if (validFileTypes.includes(file.type)) {
            reader.readAsBinaryString(file);
          }
          else {
            this.showDialog(fileTypeErrorPopupCode);
            subscriber.error();
          }
        }
      }
      catch (error) {
        subscriber.error(error);
      }
    });
  }

  private postSheetData<TEntity>(subscriber: Subscriber<void>, endpoint: string, entities: TEntity[], successPopupCode: string): void {
    this.appDataService.post(endpoint, entities)
      .pipe(take(1), this.errorService.errorHandler())
      .subscribe({
        next: data => {
          this.spinnerService.hideSpinner();
          if (data.message) {
            const placeholders = { maxBulkOperationItemCount: this.appDataService.getAppData(APP_DATA_KEYS.maxBulkOperationItemCount) };
            this.showDialog(data.message, data.details, placeholders);
            subscriber.error();
          }
          else {
            this.showDialog(successPopupCode);
            subscriber.next();
            subscriber.complete();
          }
        },
        error: error => {
          subscriber.error(error);
        }
      });
  }

  private isSheetValid(
    subscriber: Subscriber<void>,
    sheet: ParsedExcelSheet,
    hasExpectedHeaderErrorPopupCode: string,
    hasOnlyExpectedHeaderErrorPopupCode: string,
    hasDataErrorPopupCode: string): boolean {

    if (!sheet.hasExpectedHeader) {
      this.showDialog(hasExpectedHeaderErrorPopupCode, this.getExpectedHeaders(sheet));
      subscriber.error();
      return false;
    }

    if (!sheet.hasOnlyExpectedHeader) {
      this.showDialog(hasOnlyExpectedHeaderErrorPopupCode, this.getExpectedHeaders(sheet));
      subscriber.error();
      return false;
    }

    if (!sheet.hasData) {
      this.showDialog(hasDataErrorPopupCode);
      subscriber.error();
      return false;
    }

    if (sheet.firstColumnValidationError) {
      const error = sheet.firstColumnValidationError;
      const errorDetails = this.getRowNumber(sheet);
      if (error.errorCode) {
        this.showDialog(error.errorCode, errorDetails);
      }
      else {
        // Generic message if the errorCode has not been provided
        const errorMessage = {
          [ExcelSheetColumnValidationErrorType.Mandatory]: `The upload file contains at least one row with an empty value in the ${error.headerName} column.`,
          [ExcelSheetColumnValidationErrorType.DataType]: `The upload file contains at least one row with an invalid value in the ${error.headerName} column.`
        }[error.errorType];
        this.dialogService.showSimplePopup(
          `<p>${errorMessage}</p><p>Please rectify the error and try again.<p>${this.toHtmlString(errorDetails)}`,
          'File Upload Validation Error',
          'OK');
      }
      subscriber.error();
      return false;
    }

    return true;
  }

  private showDialog(popupCode: string, data?: MessagePlaceholders, placeholders?: MessagePlaceholders): void {
    this.dialogHelperService.showDialog(popupCode, placeholders, data);
  }

  private toHtmlString(details: MessagePlaceholders): string {
    return this.dialogHelperService.toHtmlString(details);
  }

  private getRowNumber(sheet: ParsedExcelSheet): MessagePlaceholders {
    return this.getMessagePlaceholders('Row Number', sheet?.firstColumnValidationError?.rowNumber?.toString());
  }

  private getExpectedHeaders(sheet: ParsedExcelSheet): MessagePlaceholders {
    return this.getMessagePlaceholders('Expected Headers', sheet?.expectedConfiguration?.columns?.map(column => column?.headerName)?.join(', '));
  }

  private getMessagePlaceholders(key: string, value: string): MessagePlaceholders {
    return (key && value)
      ? { [key]: value }
      : null;
  }
}
